// Story 1.31 — TanStack Query mutation:
// POST /api/v1/admin/moderation/flags/:id/resolve.
//
// One mutation covers all three actions (replace_image, suspend_creator,
// dismiss); the action discriminant + optional reason are passed in
// the body. On 200, invalidates the moderation queue. On 409
// MODERATION_FLAG_ALREADY_RESOLVED, refetches the queue so the stale
// row disappears. On 422 (mismatch / suspend-not-applicable) and 400
// (validation), the error code is bubbled to the caller for inline
// toast handling.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type ResolveModerationAction =
  | "replace_image"
  | "suspend_creator"
  | "dismiss";

export type ResolveModerationFlagInput = {
  flag_id: string;
  action: ResolveModerationAction;
  /** Required when action === 'suspend_creator' (20-500 chars). */
  reason?: string;
};

export type ResolveModerationFlagResponse = {
  data: {
    flag_id: string;
    status: "actioned" | "dismissed";
    decision: ResolveModerationAction;
    resolved_at: string;
  };
};

export class ResolveModerationFlagError extends Error {
  code?: string;
  current_status?: string;
  target_kind?: string;
  attempted?: string;
  user_id?: string;
  field?: string;

  constructor(message: string) {
    super(message);
    this.name = "ResolveModerationFlagError";
  }
}

async function resolveFlag(
  input: ResolveModerationFlagInput
): Promise<ResolveModerationFlagResponse> {
  const { flag_id, ...body } = input;
  const response = await fetch(
    `/api/v1/admin/moderation/flags/${flag_id}/resolve`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Resolve moderation flag failed (HTTP ${response.status})`;
    let parsed: Record<string, unknown> | undefined;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
      parsed = raw?.error;
    } catch {
      // body not JSON
    }
    const err = new ResolveModerationFlagError(message);
    err.code = parsed?.code as string | undefined;
    err.current_status = parsed?.current_status as string | undefined;
    err.target_kind = parsed?.target_kind as string | undefined;
    err.attempted = parsed?.attempted as string | undefined;
    err.user_id = parsed?.user_id as string | undefined;
    err.field = parsed?.field as string | undefined;
    throw err;
  }
  return (await response.json()) as ResolveModerationFlagResponse;
}

export function useResolveModerationFlag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resolveFlag,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-moderation-flags"] });
      qc.invalidateQueries({ queryKey: ["admin-moderation-flag"] });
    },
    onError: () => {
      // ResolveModerationFlagError carries `.code` so the page can
      // branch the toast copy. 409 ALREADY_RESOLVED also benefits from
      // invalidation so the stale row drops out of the list.
      qc.invalidateQueries({ queryKey: ["admin-moderation-flags"] });
    },
  });
}
